import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { ChevronRightIconLarge } from "../icons/ChevronRightIcon";
import { useContentVisibility } from "@/helpers/Providers";
import clsx from "clsx";
import ProductMegaMenu from "./ProductMegaMenu";
import ResourcesMegaMenu from "./ResourcesMegaMenu";
import AboutMegaMenu from "./AboutMegaMenu";

// Layout is structural data; changing a visible label never selects a renderer.
function isProductMenu(item: any): boolean { return item?.layout === "product"; }
function isResourcesMenu(item: any): boolean { return item?.layout === "resources"; }
function isAboutMenu(item: any): boolean { return item?.layout === "about"; }
// Any of the three custom-layout mega-menus (Product, Resources, About).
function isCustomMegaMenu(item: any): boolean {
  return isProductMenu(item) || isResourcesMenu(item) || isAboutMenu(item);
}

// The custom mega-menus (Product, Resources, About) already size themselves
// to their own columns via `xl:w-fit` — this is only a safety ceiling so an
// unusually wide authored case can't blow past the viewport. It isn't a
// per-menu target width, so one shared value covers all three: Product (rail
// + links + promo) is the widest legitimate layout, at roughly 1100px.
const CUSTOM_MEGA_MENU_MAX_WIDTH = 1180;


// Throttle hook to limit function call frequency
function useThrottle<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  const lastCall = useRef(0);
  
  return useCallback((...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCall.current >= delay) {
      fn(...args);
      lastCall.current = now;
    }
  }, [fn, delay]);
}

function MobileSlidePanels({ sections, openSection, setOpenSection, clickSubmenu }: {
  sections: any[];
  openSection: string | null;
  setOpenSection: (key: string | null) => void;
  clickSubmenu: (link: string, external?: boolean) => void;
}) {
  const activeSection = openSection ? sections.find((s: any) => s.id === openSection) : null;
  return (
    <div className="w-full overflow-x-hidden">
      <div
        className="flex transition-transform duration-300 ease-in-out"
        style={{ width: "200%", transform: openSection ? "translateX(-50%)" : "translateX(0)" }}
      >
        {/* Panel 1: section title list */}
        <div className="w-1/2">
          {sections.map((section: any) =>
            section.mobilePresentation === "drilldown" ? (
              <button key={section.id} className="flex justify-between items-center w-full py-4" onClick={() => setOpenSection(section.id)}>
                <span className="text-white text-2xl font-semibold leading-tight">{section.title}</span>
                <ChevronRightIconLarge />
              </button>
            ) : (
              <div key={section.id} className="flex flex-col gap-3 py-2">
                {section.links?.map((link: any) => (
                  <Link key={link.id} href={link.link} target={link.external ? "_blank" : undefined} onClick={() => clickSubmenu(link.link, link.external)}
                    className="text-white self-start leading-[112%] group gap-2 flex items-center font-medium tracking-[1%] transition-colors duration-150">
                    <span className="group-hover:text-white/70 transition-colors duration-150">{link.title}</span>
                  </Link>
                ))}
              </div>
            )
          )}
        </div>

        {/* Panel 2: selected section links */}
        <div className="w-1/2">
          {activeSection?.links && (
            <div className="flex flex-col gap-3">
              {activeSection.links.map((link: any) => (
                <Link key={link.id} href={link.link} target={link.external ? "_blank" : undefined} onClick={() => clickSubmenu(link.link, link.external)}
                  className="text-white self-start leading-[112%] group gap-2 flex items-center font-medium tracking-[1%] transition-colors duration-150">
                  <span className="group-hover:text-white/70 transition-colors duration-150">{link.title}</span>
                  {link.label && (
                    <div className="bg-[#FCEA981F] text-yellow text-[9px] leading-none p-2 rounded-sm font-mono uppercase">{link.label}</div>
                  )}
                </Link>
              ))}
            </div>
          )}
          {activeSection?.groups && (
            <div className="flex flex-col">
              {activeSection.links && <div className="mt-3" />}
              {activeSection.groups.map((group: any, idx: number) => (
                <div key={group.id}>
                  {idx > 0 && <div className="mb-5" />}
                  {group.title && (
                    <div className="mb-3">
                      <span className="text-grey-600 text-[11px] uppercase font-semibold tracking-wider leading-none">{group.title}</span>
                    </div>
                  )}
                  <div className="flex flex-col gap-3">
                    {group.links.map((link: any) => (
                      <Link key={link.id} href={link.link} target={link.external ? "_blank" : undefined} onClick={() => clickSubmenu(link.link, link.external)}
                        className="text-white self-start leading-[112%] group gap-2 flex items-center font-medium tracking-[1%] transition-colors duration-150">
                        <span className="group-hover:text-white/70 transition-colors duration-150">{link.title}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function NavigationBar({
  menu,
  toggleNavigation,
  setIsOpen,
  submenuOpen,
  setSubmenuOpen,
  navBackHandlerRef,
  currentlyOpen,
  banner,
  showCTAButtons,
  isLoggedIn
}: any) {

  // Simplified state management with useState hooks
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [transitionDropdown, setTransitionDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [openSection, setOpenSection] = useState<string | null>(null);
  
  const { hide } = useContentVisibility();
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Always mirrors the latest hoveredItem, so a requestAnimationFrame-deferred
  // calculatePosition() call from a previous dropdown can detect it's stale
  // (the user already switched to a different dropdown) and skip itself.
  // Assigned in an effect rather than during render: effects flush before
  // paint and rAF callbacks run after it, so the deferred read still sees the
  // current value.
  const latestHoveredItemRef = useRef(hoveredItem);
  useEffect(() => {
    latestHoveredItemRef.current = hoveredItem;
  }, [hoveredItem]);

  // Memoize the dropdown item finder function
  const findDropdownItem = useCallback((key: string) => {
    return menu.find((item: any) => item.id === key);
  }, [menu]);

  // Check for mobile view
  useEffect(() => {
    const checkMobile = () => window.innerWidth < 1280;
    setIsMobile(checkMobile());

    // Debounced resize handler
    let resizeTimer: NodeJS.Timeout;
    const resizeHandler = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setIsMobile(checkMobile());
      }, 100);
    };

    window.addEventListener("resize", resizeHandler, { passive: true });
    
    return () => {
      window.removeEventListener("resize", resizeHandler);
    };
  }, []);

  // Refs for timeouts to allow clearing them
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const clearDropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Show dropdown function
  const showMoreImpl = useCallback((key: string) => {
    // Clear any pending timeouts to prevent state conflicts
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }
    if (clearDropdownTimeoutRef.current) {
      clearTimeout(clearDropdownTimeoutRef.current);
      clearDropdownTimeoutRef.current = null;
    }
    
    // If we're already showing a dropdown, update immediately without animations
    if (openDropdown) {
      setOpenDropdown(key);
      setHoveredItem(key);
      setTransitionDropdown(true);
    } else {
      // Normal flow for initial dropdown opening
      setSubmenuOpen(true);
      setOpenDropdown(key);
      setHoveredItem(key);
      
      // Set transition after a short delay
      transitionTimeoutRef.current = setTimeout(() => {
        setTransitionDropdown(true);
        transitionTimeoutRef.current = null;
      }, 50);
    }
  }, [openDropdown, setSubmenuOpen]);

  // Hide dropdown function
  const hideMoreImpl = useCallback(() => {
    // Clear any pending timeouts
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }
    if (clearDropdownTimeoutRef.current) {
      clearTimeout(clearDropdownTimeoutRef.current);
      clearDropdownTimeoutRef.current = null;
    }
    
    setTransitionDropdown(false);
    setSubmenuOpen(false);

    // Clear dropdown after transition
    clearDropdownTimeoutRef.current = setTimeout(() => {
      if (openDropdown) {
        setOpenDropdown(null);
      }
      clearDropdownTimeoutRef.current = null;
    }, 150);
  }, [openDropdown, setSubmenuOpen]);
  
  // Throttled version for mouse events
  const hideMore = useThrottle(hideMoreImpl, 100);
  
  // Throttled hover handler
  const handleHover = useThrottle((key: string | null) => {
    setHoveredItem(key);
  }, 50);

  // Handle submenu state changes
  useEffect(() => {
    if (!submenuOpen) {
      hideMore();
    }
  }, [hideMore, submenuOpen]);

  // Reset section open states when dropdown changes
  useEffect(() => {
    setOpenSections({});
    setOpenSection(null);
  }, [openDropdown]);

  // Keep navBackHandlerRef in sync: when Panel 2 is open, intercept the mobile back button
  useEffect(() => {
    if (!navBackHandlerRef) return;
    navBackHandlerRef.current = openSection
      ? () => { setOpenSection(null); return true; }
      : null;
  }, [openSection, navBackHandlerRef]);

  // Handle submenu click
  const clickSubmenu = useCallback((link: string, external?: boolean) => {
    // hide current content only if its not an external link
    if (!external && link) hide(link);
    setSubmenuOpen(false);
    setOpenDropdown(null);
    setIsOpen(false);
  }, [hide, setIsOpen, setSubmenuOpen]);

  // Dropdown positioning
  const calculatePosition = useCallback(() => {
    if (!hoveredItem || !dropdownRef.current) return;
    if (latestHoveredItemRef.current !== hoveredItem) return;

    const menuItem = document.getElementById(`dropdown_container_${hoveredItem}`);
    const dropdown = dropdownRef.current;

    if (menuItem && dropdown) {
      dropdown.style.left = `${menuItem.getBoundingClientRect().left - 28}px`;
    }
  }, [hoveredItem]);

  // Consolidated position calculation effect
  useEffect(() => {
    if (hoveredItem && dropdownRef.current) {
      calculatePosition();
      requestAnimationFrame(calculatePosition);
    }
  }, [hoveredItem, openDropdown, transitionDropdown, calculatePosition]);

  // Simplified footer items construction
  const footerItems = [
    ...(showCTAButtons ? [{title: "Book A Demo", url: "/book-a-demo"}] : []),
    ...(showCTAButtons 
      ? (isLoggedIn 
          ? [{title: "Open Dash0", url: "https://app.dash0.com"}] 
          : [
              {title: "Sign In", url: "https://app.dash0.com"},
              {title: "Start Free Trial", url: "/sign-up"},
            ])
      : [])
  ];

  return (
    <nav
      className="flex landscape:overflow-y-auto hide-scrollbar transition-all xl:transition-none xl:items-center xl:flex-row flex-col xl:bg-transparent xl:p-0 p-6 rounded-lg bg-grey-900 xl:static gap-6 w-full h-full justify-start font-medium"
      id="navigation"
    >
      {menu.map((item: any) => {
        // Determine if this item is a dropdown or a regular link
        const isDropdown = item.layout !== "link";
        const isActive = openDropdown === item.id;
        const isCurrentPage = currentlyOpen === item.link;
        
        // Common classes for both dropdown and regular links
        const commonClasses = "shrink-0 xl:text-base xl:font-medium font-normal text-2xl transition-all xl:transition-none duration-100";
        
        // Visibility classes based on dropdown state
        const visibilityClasses = openDropdown 
          ? "xl:opacity-100 opacity-0 pointer-events-none xl:pointer-events-auto" 
          : "opacity-100";
        
        // Text color classes
        const textColorClasses = isDropdown
          ? (isActive ? "xl:text-white" : "xl:text-[#9E9E9E] xl:hover:text-white")
          : (isCurrentPage ? "text-white" : "xl:text-[#9E9E9E] hover:text-white");
        
        return isDropdown ? (
          <div
            key={"dropdown_"+ item.id}
            id={"dropdown_container_" + item.id}
            className={clsx("transition-opacity duration-150 relative", visibilityClasses)}
          >
            <button
              onClick={() => showMoreImpl(item.id)}
              onMouseOver={(e) => {
                e.preventDefault();
                if (!isMobile) showMoreImpl(item.id);
                handleHover(item.id);
              }}
              onMouseLeave={() => handleHover(null)}
              id={"dropdown_button_" + item.id}
              className={clsx(
                commonClasses,
                "xl:cursor-default justify-between w-full flex items-center gap-1",
                textColorClasses
              )}
            >
              <span className="relative top-px">{item.title}</span>
              <div className="inline-flex transition-transform duration-300" style={{ willChange: 'transform' }}>
                {/* Desktop dropdown indicator */}
                <div className="hidden ml-0.5 xl:block">
                  <div className="h-3 aspect-square flex relative items-center">
                    <div
                      className={clsx(
                        "h-[1.5px] w-[62%] transition-all shrink-0 absolute left-0",
                        isActive && transitionDropdown ? "xl:-rotate-45" : "rotate-45",
                        isActive ? "bg-white" : "bg-[#9E9E9E]"
                      )}
                    />
                    <div
                      className={clsx(
                        "h-[1.5px] w-[62%] transition-all shrink-0 absolute right-0",
                        isActive && transitionDropdown ? "xl:rotate-45" : "-rotate-45",
                        isActive ? "bg-white" : "bg-[#9E9E9E]"
                      )}
                    />
                  </div>
                </div>
                {/* Mobile dropdown indicator */}
                <div
                  className={clsx(
                    "xl:hidden transition-all relative duration-300",
                    isActive ? "-left-full" : "left-0"
                  )}
                >
                  <ChevronRightIconLarge />
                </div>
              </div>
            </button>
            {/* Visually hidden submenu links for SEO/crawling purposes */}
            <div tabIndex={-1} className="sr-only">
              {!submenuOpen && item?.sections.map((section: any) =>
                section.links?.map((link: any) => (
                  <a
                    key={"seo_" + link.id}
                    href={link.link}
                    tabIndex={-1}
                  >
                    {link.title}
                  </a>
                ))
              )}
            </div>
          </div>
        ) : (
          <Link
            id="nav-item"
            href={item.link || "/"}
            onClick={() => {
              hide(item.link);
              if (item.link && item.link.includes("#")) {
                setSubmenuOpen(false);
                setOpenDropdown(null);
                setIsOpen(false);
              }
            }}
            onMouseOver={hideMore}
            key={"link_" + item.id}
            className={clsx(
              commonClasses,
              "truncate",
              visibilityClasses,
              textColorClasses
            )}
          >
            <span className="relative top-px truncate">{item.title}</span>
          </Link>
        );
      })}

      {/* Dropdown overlay and content */}
      {openDropdown && (
        <div
          onMouseOver={hideMore}
          className={clsx(
            "xl:fixed left-0 top-0 h-full xl:bg-black/32 w-full xl:z-[-1] transition-all xl:backdrop-blur-sm flex justify-center items-start",
            transitionDropdown ? "opacity-100" : "opacity-0",
            banner && "xl:pt-10"
          )}
        >
          <div
            onMouseOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className={clsx(
              "xl:relative absolute px-2 bottom-2 xl:bottom-auto xl:pt-1 group-hover:pointer-events-auto z-10 xl:px-3 w-full",
              banner ? "xl:top-[60px] top-[115px]" : "xl:top-[60px] top-[71px]"
            )}
          >
            {/* Overlay for closing dropdown */}
            <div
              className="absolute inset-0 top-1 hidden xl:block"
              onMouseOver={hideMore}
            />

            {/* Mobile gradient overlays */}
            <div className="xl:hidden h-12 rounded-b-lg absolute z-30 bottom-0 left-2 right-2 pointer-events-none bg-linear-to-b from-transparent to-grey-900" />
            <div className="xl:hidden h-12 rounded-t-lg absolute z-30 top-0 left-2 right-2 pointer-events-none bg-linear-to-t from-transparent to-grey-900" />
            
            {/* Dropdown content container */}
            <div
              ref={dropdownRef}
              style={{
                maxWidth: !isMobile
                  ? isCustomMegaMenu(findDropdownItem(openDropdown))
                    ? CUSTOM_MEGA_MENU_MAX_WIDTH
                    : findDropdownItem(openDropdown).sections.length * 240 +
                      (findDropdownItem(openDropdown).sections.find(
                        (section: any) => section.links && section.links.length > 15
                      ) ? 250 : 50)
                  : undefined,
                willChange: 'opacity, transform'
              }}
              id="dropdown"
              className={clsx(
                "xl:overflow-y-hidden overflow-y-auto h-full hide-scrollbar px-6 py-8 xl:pt-5 xl:pb-8 xl:px-[15px] relative transition-[left,top] duration-300 xl:duration-300 overflow-hidden xl:overflow-x-auto hide-scrollbar xl:w-fit",
                !isMobile && isCustomMegaMenu(findDropdownItem(openDropdown)) ? "" : "bg-grey-900 rounded-lg",
                transitionDropdown ? "xl:top-0 left-0 xl:left-auto ease-out" : "xl:-top-4 left-full xl:left-auto ease-in"
              )}
            >
              {!isMobile && isProductMenu(findDropdownItem(openDropdown)) ? (
                <ProductMegaMenu
                  clickSubmenu={clickSubmenu}
                  sections={findDropdownItem(openDropdown).sections}
                  footerLabel={findDropdownItem(openDropdown).footerLabel}
                  footerCta={findDropdownItem(openDropdown).footerCta}
                />
              ) : !isMobile && isResourcesMenu(findDropdownItem(openDropdown)) ? (
                <ResourcesMegaMenu
                  clickSubmenu={clickSubmenu}
                  sections={findDropdownItem(openDropdown).sections}
                  footerLabel={findDropdownItem(openDropdown).footerLabel}
                  footerCta={findDropdownItem(openDropdown).footerCta}
                />
              ) : !isMobile && isAboutMenu(findDropdownItem(openDropdown)) ? (
                <AboutMegaMenu
                  clickSubmenu={clickSubmenu}
                  sections={findDropdownItem(openDropdown).sections}
                  footerLabel={findDropdownItem(openDropdown).footerLabel}
                  footerCta={findDropdownItem(openDropdown).footerCta}
                />
              ) : (
                <>
              {/* Mobile: slide panel navigation */}
              {isMobile && (
                <MobileSlidePanels
                  sections={findDropdownItem(openDropdown).sections}
                  openSection={openSection}
                  setOpenSection={setOpenSection}
                  clickSubmenu={clickSubmenu}
                />
              )}

              {/* Desktop: columns layout */}
              {!isMobile && (
              <div className="flex gap-0 xl:w-max xl:flex-row xl:h-full max-xl:hidden">
                {findDropdownItem(openDropdown).sections.map((section: any) => {
                  const type = section.groups ? "groups" : section.links ? "links" : "feature";

                  // Determine section styling
                  const sectionClasses = (() => {
                    const sections = findDropdownItem(openDropdown).sections;
                    const isLastSection = section.id === sections[sections.length - 1].id;
                    const nextSectionHasLinks = !isLastSection &&
                      (sections[findDropdownItem(openDropdown).sections.indexOf(section) + 1].links ||
                       sections[findDropdownItem(openDropdown).sections.indexOf(section) + 1].groups);

                    if (!isLastSection && type !== "feature" && nextSectionHasLinks) {
                      return "xl:border-r xl:border-r-grey-850 xl:pr-[15px] xl:mr-[15px]";
                    }

                    return type === "feature" ? "xl:mr-16" : "";
                  })();
                  
                  const isCollapsible = isMobile && section.mobilePresentation === "drilldown" && type !== "feature";
                  const isSectionOpen = !isCollapsible || !!openSections[section.id];

                  return (
                    <div key={section.id} className="xl:relative max-xl:w-full">
                      <div
                        style={{
                          minHeight: "fit-content",
                          height: isMobile && type !== "feature" && isSectionOpen
                            ? section.links
                              ? section.links.length * 28
                              : section.groups
                                ? section.groups.reduce((acc: number, g: any) => acc + g.links.length * 28 + 40, 0)
                                : 240
                            : undefined,
                        }}
                        className={clsx(
                          "flex flex-col items-start h-full shrink-0 xl:h-full xl:mb-auto xl:w-[208px] max-xl:w-full",
                          sectionClasses
                        )}
                      >
                        {section.title && section.title.length > 0 ? (
                          <div
                            className={clsx(
                              "flex items-start w-full gap-3 xl:static xl:z-0 z-20 max-xl:top-0",
                              section.description ? "xl:mb-6 flex-col" : "xl:mb-5 items-center",
                              isSectionOpen && "mb-6",
                              isCollapsible && "cursor-pointer xl:cursor-default max-xl:py-4 xl:py-0 max-xl:flex-row max-xl:items-center max-xl:justify-between"
                            )}
                            onClick={isCollapsible ? () => setOpenSections(prev => ({ ...prev, [section.id]: !prev[section.id] })) : undefined}
                          >
                            {type === "feature" ? (
                              <Link
                                onClick={() => clickSubmenu(section.link)}
                                key={section.link.id}
                                href={section.link}
                                className={clsx(
                                  "xl:h-full flex items-center justify-center w-full group h-40 xl:w-[208px]",
                                  type === "feature" ? "" : "min-h-[295px]"
                                )}
                              >
                                <div className="inset-0 z-20 xl:max-h-none max-xl:max-h-56 absolute bg-linear-to-t from-black/40 via-black/20 to-transparent" />
                                {section.badge && (
                                  <div className="absolute font-mono z-20 top-8 left-6 xl:top-4 uppercase tracking-widest xl:left-4 leading-none p-2 bg-red-900 text-red-500 text-[9px] rounded-sm font-semibold">
                                    {section.badge}
                                  </div>
                                )}
                                <div className="inset-0 overflow-hidden xl:max-h-none max-xl:max-h-56 absolute">
                                  <Image
                                    src={section.cover.src}
                                    width={512}
                                    height={240}
                                    loading="lazy"
                                    sizes="(max-width: 1280px) 100vw, 240px"
                                    className="h-full transition-transform aspect-120/197 duration-200 group-hover:scale-[1.02] xl:top-0 xl:left-0 z-10 w-full absolute max-xl:max-h-56 object-cover xl:max-h-none max-xl:inset-0 top-0"
                                    alt={section.title}
                                  />
                                </div>
                                <div className="absolute left-4 right-8 xl:right-auto xl:inset-0 xl:bottom-0 max-xl:top-56 w-full xl:w-[240px] z-20">
                                  <h3 className="text-white absolute bottom-0 left-4 right-8 xl:text-base xl:right-auto xl:max-w-[calc(240px-16px)] text-balance lead font-normal">
                                    {section.title}
                                  </h3>
                                </div>
                              </Link>
                            ) : section.description ? (
                              <div className="flex flex-col gap-1">
                                <h3 className="text-white font-semibold xl:text-base text-2xl leading-tight m-0">
                                  {section.title}
                                </h3>
                                <p className="text-grey-400 text-[11px] leading-snug m-0 hidden xl:block">
                                  {section.description}
                                </p>
                              </div>
                            ) : (
                              <div className="h-5 flex items-center gap-3">
                                {section.icon && (
                                  <Image
                                    src={section.icon}
                                    width={20}
                                    height={20}
                                    loading="lazy"
                                    alt={section.title}
                                    className="xl:block hidden"
                                  />
                                )}
                                <h3 className="xl:text-grey-600 text-white m-0 xl:text-[11px] text-2xl relative z-10 xl:leading-none leading-tight xl:uppercase xl:tracking-wider font-semibold">
                                  {section.title}
                                </h3>
                              </div>
                            )}
                            {isCollapsible && (
                              <div className={clsx("xl:hidden ml-auto transition-transform duration-200 shrink-0", isSectionOpen ? "rotate-90" : "rotate-0")}>
                                <ChevronRightIconLarge />
                              </div>
                            )}
                          </div>
                        ) : undefined}

                        {section.links && isSectionOpen && (
                          <div
                            style={{
                              width: section.links.length > 15 ? "416px" : undefined,
                            }}
                            className={clsx(
                              section.links.length > 15
                                ? "flex flex-col xl:flex-wrap max-h-44 gap-x-8 xl:gap-y-2 gap-y-4"
                                : "flex flex-col xl:gap-3 gap-3"
                            )}
                          >
                            {section.links.map((link: any) => (
                              <Link
                                id="nav-item"
                                href={link.link}
                                target={link.external ? "_blank" : undefined}
                                onClick={() => clickSubmenu(link.link, link.external)}
                                key={link.id}
                                className="text-white self-start leading-[112%] group gap-2 xl:text-sm flex items-center font-medium tracking-[1%] transition-colors duration-150"
                              >
                                <span className="group-hover:text-white/70 transition-colors duration-150">
                                  {link.title}
                                </span>
                                {link.label && (
                                  <div className="bg-[#FCEA981F] hidden xl:block text-yellow text-[9px] leading-none p-2 rounded-sm font-mono uppercase">
                                    {link.label}
                                  </div>
                                )}
                              </Link>
                            ))}
                          </div>
                        )}

                        {section.groups && isSectionOpen && (
                          <div className="flex flex-col w-full">
                            {section.links && <div className="border-t border-grey-800 mt-3 mb-5" />}
                            {section.groups.map((group: any, idx: number) => (
                              <div key={group.id}>
                                {idx > 0 && (
                                  <div className="border-t border-grey-800 mt-6 mb-5" />
                                )}
                                <div className="mb-3">
                                  <span className="text-grey-600 text-[11px] uppercase font-semibold tracking-wider leading-none">
                                    {group.title}
                                  </span>
                                </div>
                                <div className="flex flex-col xl:gap-3 gap-3">
                                  {group.links.map((link: any) => (
                                    <Link
                                      id="nav-item"
                                      href={link.link}
                                      target={link.external ? "_blank" : undefined}
                                      onClick={() => clickSubmenu(link.link, link.external)}
                                      key={link.id}
                                      className="text-white self-start leading-[112%] group gap-2 xl:text-sm flex items-center font-medium tracking-[1%] transition-colors duration-150"
                                    >
                                      <span className="group-hover:text-white/70 transition-colors duration-150">
                                        {link.title}
                                      </span>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {section.button && (
                          <>
                            <div className="h-full min-h-20 xl:block hidden" />
                            <Link
                              id="nav-item"
                              onClick={() => clickSubmenu(section.button.link)}
                              href={section.button.link || "/"}
                              passHref
                              className="px-2 py-1.5 hidden xl:block text-sm bg-grey-850 rounded leading-none transition-colors duration-150 hover:bg-grey-800"
                            >
                              {section.button.title}
                            </Link>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div key="spacer" className="xl:hidden h-full" />
      
      <ul
        className={clsx(
          "xl:hidden transition-opacity duration-100",
          openDropdown
            ? "xl:opacity-100 opacity-0 pointer-events-none xl:pointer-events-auto"
            : "opacity-100"
        )}
      >
        {footerItems.map((item: any, index: number) => (
          <li key={"footer_"+ index} onClick={toggleNavigation}>
            <Link
              href={item.url || "/"}
              key={"footer_link_" + index}
              className="xl:text-[#9E9E9E] leading-none mt-3 py-1 xl:text-base xl:font-medium font-normal text-white text-2xl hover:text-white transition-colors duration-150 truncate"
            >
              {item.title}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
